import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 免费91网页`;
    }
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: article.categorySlug, path: `/category/${article.categorySlug}` },
    { name: article.slug },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '免费91网页',
    },
    publisher: {
      '@type': 'Organization',
      name: '免费91网页',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.free91.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://www.free91.com/category/${article.categorySlug}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 max-w-3xl mx-auto py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                发布于 {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读时间约 {article.readTime} 分钟
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-bookmark-line"></i>
                {article.category}
              </span>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-400">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-4">
              相关文章推荐
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {articles
                .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group flex gap-3 p-3 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-background-200/70 text-center">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{article.category}列表
            </Link>
          </div>
        </article>
      </main>
    </>
  );
}